import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import { STEPS, stateOptions } from '../commonStateTaxData';
import {
  stepOneInitialQuestions,
  getWorksheetInitialQuestions,
  additionalAmountInitialQuestion,
  filingStatusInitialQuestion,
  getYear,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import * as S from '../../styles';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function LegacyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFirstNameMiddleInitial,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_first_name_middle_initial = formatFirstNameMiddleInitial();

  const stepOneForm = useRef();
  const stepOneQuestionsWithAnswers = getInitialQuestions(
    stepOneInitialQuestions,
  );
  const [stepOneQuestions, setStepOneQuestions] = useState(
    stepOneQuestionsWithAnswers,
  );

  const [lastNameDifferentOnSSN, setLastNameDifferentOnSSN] = useState(
    data.last_name_different_on_social_security_card || false,
  );

  const filingStatusForm = useRef();
  const filingStatusQuestionWithAnswer = getInitialQuestions(
    filingStatusInitialQuestion,
  );
  const [filingStatusQuestion, setFilingStatusQuestion] = useState(
    filingStatusQuestionWithAnswer,
  );

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(
    data.is_exempt || false,
  );
  const [exemptionsFromWithholding, setExemptionsFromWithholding] = useState(
    data.exemption || '',
  );
  const [exemptionsError, setExemptionsError] = useState(false);

  const isWorksheetFilled = () => (
    data.file_yourself
      || data.file_married_filing_jointly
      || data.file_head_of_household
      || data.file_others
      || data.deductions_adjustments_additional_income_worksheet_value
      || data.dependents
      || data.dependents_under_age_6
  );

  const [worksheetOpen, setWorksheetOpen] = useState(
    isWorksheetFilled() || false,
  );
  const [fileForYourself, setFileForYourself] = useState(
    data.file_yourself || false,
  );
  const [fileAsMarriedJointly, setFileAsMarriedJointly] = useState(
    data.file_married_filing_jointly || false,
  );
  const [fileAsHeadOfHousehold, setFileAsHeadOfHousehold] = useState(
    data.file_head_of_household || false,
  );
  const [fileOthers, setFileOthers] = useState(data.file_others || false);

  const worksheetForm = useRef();

  const year = getYear(version);
  const worksheetInitialQuestions = getWorksheetInitialQuestions(year);
  const worksheetQuestionsWithAnswers = getInitialQuestions(
    worksheetInitialQuestions,
  );
  const [worksheetQuestions, setWorksheetQuestions] = useState(
    worksheetQuestionsWithAnswers,
  );

  useEffect(() => {
    setWorksheetQuestions(worksheetQuestionsWithAnswers);
  }, [version]);

  const [totalNoOfAllowances, setTotalNoOfAllowances] = useState(
    data.total_number_of_allowances || '',
  );
  const [totalNoOfAllowancesError, setTotalNoOfAllowancesError] = useState(false);
  const [deductions, setDeductions] = useState(
    data.deductions_adjustments_additional_income_worksheet_value || '',
  );
  const [deductionsError, setDeductionsError] = useState(false);

  const additionalAmountForm = useRef();
  const additionalAmountQuestionWithAnswer = getInitialQuestions(
    additionalAmountInitialQuestion,
  );
  const [additionalAmountQuestion, setAdditionalAmountQuestion] = useState(
    additionalAmountQuestionWithAnswer,
  );

  const [stateOfDomicile, setStateOfDomicile] = useState(
    data.state_of_domicile || '',
  );
  const [stateOfDomicileError, setStateOfDomicileError] = useState(false);

  const calculatePersonalAllowances = () => {
    let value = 0;
    if (fileForYourself) value += 1;
    if (fileAsMarriedJointly) value += 1;
    if (fileAsHeadOfHousehold) value += 1;
    if (fileOthers) value += 1;

    const { dependents } = worksheetQuestions[0].answer.answer;
    const dependentsUnderAge6 = worksheetQuestions[1].answer.answer.dependents_under_age_6;
    if (dependents) value += parseInt(dependents);
    if (dependentsUnderAge6) value += parseInt(dependentsUnderAge6);

    return value;
  };

  const calculateTotalNoOfAllowances = () => {
    const personalAllowances = calculatePersonalAllowances();
    return deductions
      ? personalAllowances + parseInt(deductions)
      : personalAllowances;
  };

  const renderStep1Form = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={stepOneForm}
      />
      <Flex column>
        <S.QuestionWrapper mb="20px">
          <Flex>
            <label>
              <Flex>
                <S.Input
                  style={{ height: '20px' }}
                  type="checkbox"
                  value={lastNameDifferentOnSSN}
                  onChange={(e) => setLastNameDifferentOnSSN(e.target.checked)}
                  checked={lastNameDifferentOnSSN}
                  disabled={isReviewStep}
                />
                <Box ml={1} />
                Check if your last name is different on your Social Security
                card.
              </Flex>
            </label>
          </Flex>
        </S.QuestionWrapper>
      </Flex>
      <FormConsumer
        questions={filingStatusQuestion}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = filingStatusQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setFilingStatusQuestion(questions);
        }}
        ref={filingStatusForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding. (Line 7)
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel isRequired={isExemptFromWithholding}>
        Exemptions from withholding
      </S.QuestionLabel>
      <S.QuestionWrapper mt="10px" mb="24px">
        <label>
          <Flex mb="10px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_one"
              checked={
                  exemptionsFromWithholding === 'exemption_from_withholding_one'
                }
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            <Flex column flex={1}>
              <Text>
                For tax year
                {' '}
                {year - 1}
                , I had a right to refund
                <b> all </b>
                South Carolina Income Tax withheld because I had
                <b> no </b>
                tax liability,
                <b> and </b>
                for tax year
                {' '}
                {year}
                {' '}
                I expect a refund
                of
                <b> all </b>
                South Carolina Income Tax withheld because I
                expect to have
                <b> no </b>
                tax liability.
              </Text>
            </Flex>
          </Flex>
        </label>
        <label>
          <Flex>
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_two"
              checked={
                  exemptionsFromWithholding === 'exemption_from_withholding_two'
                }
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            <Flex column flex={1}>
              <Text>
                I elect to use the same state of residence for tax purposes as
                my military servicemember spouse. I have provided my employer
                with a copy of my current military ID card and a copy of my
                spouse's latest Leave and Earning Statement (LES).
              </Text>
            </Flex>
          </Flex>
        </label>
        {exemptionsError && exemptionsFromWithholding === '' && (
        <Flex justifyContent="flex-start" mt={1}>
          <Text color="red">This is a mandatory field.</Text>
        </Flex>
        )}
      </S.QuestionWrapper>
      {exemptionsFromWithholding === 'exemption_from_withholding_two' && (
      <Flex mb="20px" column>
        <Flex mb="7px">
          <S.QuestionLabel
            isRequired={
                  exemptionsFromWithholding === 'exemption_from_withholding_two'
                }
          >
            State of domicile:
          </S.QuestionLabel>
        </Flex>
        <Flex>
          <S.Select
            disabled={
                  exemptionsFromWithholding
                    !== 'exemption_from_withholding_two' || isReviewStep
                }
            onChange={(e) => {
              setStateOfDomicile(e.target.value);
              setStateOfDomicileError(false);
            }}
            style={{ width: '100%' }}
            value={stateOfDomicile}
          >
            <option
              value=""
              style={{ display: 'none', color: '#666' }}
              key={0}
            >
              Select from dropdown
            </option>
            {stateOptions.map((option, idx) => (
              <option value={option.codeValue} key={idx}>
                {option.shortName}
              </option>
            ))}
          </S.Select>
        </Flex>
        {stateOfDomicileError && (
        <Flex justifyContent="flex-start" mt={1}>
          <Text color="red">This is a mandatory field.</Text>
        </Flex>
        )}
      </Flex>
      )}
    </Flex>
  );

  const renderCollapsibleQuestionsContent = (isReviewStep) => (
    <div>
      <S.QuestionWrapper mb="20px">
        <Flex mb="7px">
          <S.QuestionLabel>Subtotal of personal allowances:</S.QuestionLabel>
        </Flex>
        <S.Input
          type="text"
          value={calculatePersonalAllowances()}
          disabled={worksheetOpen || isReviewStep}
        />
      </S.QuestionWrapper>
      <S.QuestionWrapper mb="20px">
        <Flex mb="7px">
          <S.QuestionLabel>
            Check the applicable boxes below to determine your personal
            allowances.
          </S.QuestionLabel>
        </Flex>
        <Flex column>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={fileForYourself}
                onChange={(e) => setFileForYourself(e.target.checked)}
                checked={fileForYourself}
                disabled={isReviewStep}
              />
              <Box ml={1} />
              For yourself (Line A)
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={fileAsMarriedJointly}
                onChange={(e) => setFileAsMarriedJointly(e.target.checked)}
                checked={fileAsMarriedJointly}
                disabled={isReviewStep}
              />
              <Box ml={1} />
              If you will file as married jointly (Line B)
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={fileAsHeadOfHousehold}
                onChange={(e) => setFileAsHeadOfHousehold(e.target.checked)}
                checked={fileAsHeadOfHousehold}
                disabled={isReviewStep}
              />
              <Box ml={1} />
              If you will file as head of household (Line C)
            </Flex>
          </label>
          <Flex mt="7px">
            <label>
              <Flex>
                <S.Input
                  style={{ height: '20px' }}
                  type="checkbox"
                  value={fileOthers}
                  onChange={(e) => setFileOthers(e.target.checked)}
                  checked={fileOthers}
                  disabled={isReviewStep}
                />
                <Box ml={1} />
                <Flex column>
                  <Text>If:</Text>
                  <Text>
                    {'\u2022'}
                    {' '}
                    You are single, or married filing separately,
                    and only have one job; or
                  </Text>
                  <Text>
                    {'\u2022'}
                    {' '}
                    You are married filing jointly, have only one
                    job, and your spouse doesn't work; or
                  </Text>
                  <Text>
                    {'\u2022'}
                    {' '}
                    Your wages from a second job or your spouse's
                    wages (or the total of both) are $1,500 or less. (Line D)
                  </Text>
                </Flex>
              </Flex>
            </label>
          </Flex>
        </Flex>
      </S.QuestionWrapper>
      <FormConsumer
        questions={worksheetQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = worksheetQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWorksheetQuestions(questions);
        }}
        ref={worksheetForm}
      />
      <S.QuestionWrapper mb="20px">
        <Flex mb="7px" column>
          <Flex mb="7px">
            <S.QuestionLabel>
              Subtotal of Deductions, Adjustments and Additional Income:
            </S.QuestionLabel>
          </Flex>
          <Text style={{ fontSize: '12px', color: 'grey' }}>
            If you plan to itemize or claim adjustments to income and want to
            reduce your withholding, or if you have a large amount of nonwage
            income not subject to withholding and want to increase your
            withholding, use the
            <span style={{ color: '#108EE9' }}>
              {' '}
              Deductions, Adjustments, and Additional Income Worksheet of the
              Form SC-W4
              {' '}
            </span>
            and enter the total from line 10 here.
          </Text>
        </Flex>
        <S.Input
          type="text"
          value={deductions}
          onChange={(e) => {
            setDeductions(e.target.value);
            setDeductionsError(false);
          }}
          disabled={isReviewStep}
        />
        {deductionsError && (
        <Flex justifyContent="flex-start">
          <Text color="red">Please enter a valid response.</Text>
        </Flex>
        )}
      </S.QuestionWrapper>
    </div>
  );

  const renderAllowances = (isReviewStep) => (
    <div>
      <S.QuestionWrapper mb="20px">
        <Flex mb="7px">
          <S.QuestionLabel>Total number of allowances (Line 5)</S.QuestionLabel>
        </Flex>
        <S.Input
          type="text"
          value={
              worksheetOpen
                ? calculateTotalNoOfAllowances()
                : totalNoOfAllowances
            }
          onChange={(e) => {
            setTotalNoOfAllowances(e.target.value);
            setTotalNoOfAllowancesError(false);
          }}
          disabled={worksheetOpen || isReviewStep}
        />
        {totalNoOfAllowancesError && (
        <Flex justifyContent="flex-start">
          <Text color="red">Please enter a valid response.</Text>
        </Flex>
        )}
      </S.QuestionWrapper>
      <CollapsibleQuestions
        title="If you need to calculate your personal allowances, use this worksheet."
        handleClick={() => {
          if (!isReviewStep) {
            setWorksheetOpen(!worksheetOpen);
            setTotalNoOfAllowancesError(false);
          }
        }}
        open={worksheetOpen || isReviewStep}
      >
        {renderCollapsibleQuestionsContent(isReviewStep)}
      </CollapsibleQuestions>
      <FormConsumer
        questions={additionalAmountQuestion}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalAmountQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalAmountQuestion(questions);
        }}
        ref={additionalAmountForm}
      />
    </div>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderAllowances()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (stepOneForm && stepOneForm.current) {
              stepOneForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (filingStatusForm && filingStatusForm.current) {
              filingStatusForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (!isExemptFromWithholding) {
              if (additionalAmountForm && additionalAmountForm.current) {
                additionalAmountForm.current.validate((error) => {
                  if (error) err = error;
                });
              }
              if (worksheetOpen) {
                setTotalNoOfAllowancesError(false);
                if (worksheetForm && worksheetForm.current) {
                  worksheetForm.current.validate((error) => {
                    if (error) err = error;
                  });
                }
                if (!/^\d*$/.test(deductions)) {
                  err = true;
                  setDeductionsError(true);
                } else {
                  setDeductionsError(false);
                }
              } else if (!/^\d*$/.test(totalNoOfAllowances)) {
                err = true;
                setTotalNoOfAllowancesError(true);
              } else {
                setTotalNoOfAllowancesError(false);
              }
            } else {
              if (exemptionsFromWithholding === '') {
                err = true;
                setExemptionsError(true);
              } else {
                setExemptionsError(false);
              }
              if (
                exemptionsFromWithholding
                  === 'exemption_from_withholding_two'
                && stateOfDomicile === ''
              ) {
                err = true;
                setStateOfDomicileError(true);
              } else {
                setStateOfDomicileError(false);
              }
            }
            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step
              const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const filingStatusData = filingStatusQuestion.reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );
              let exemptionTemp = '';
              let stateOfDomicileTemp = '';
              let totalNoOfAllowancesTemp = '0';
              let personalAllowancesTemp = '0';
              let worksheetData = resetInitialQuestions(
                worksheetInitialQuestions,
              ).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              let fileForYourselfTemp = false;
              let fileAsMarriedJointlyTemp = false;
              let fileAsHeadOfHouseholdTemp = false;
              let fileOthersTemp = false;
              let deductionsTemp = '0';
              let additionalAmountData = resetInitialQuestions(
                additionalAmountInitialQuestion,
              ).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              if (!isExemptFromWithholding) {
                if (worksheetOpen) {
                  totalNoOfAllowancesTemp = calculateTotalNoOfAllowances().toString();
                  personalAllowancesTemp = calculatePersonalAllowances().toString();
                  fileForYourselfTemp = fileForYourself;
                  fileAsMarriedJointlyTemp = fileAsMarriedJointly;
                  fileAsHeadOfHouseholdTemp = fileAsHeadOfHousehold;
                  fileOthersTemp = fileOthers;
                  worksheetData = worksheetQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                  deductionsTemp = deductions;
                } else {
                  totalNoOfAllowancesTemp = totalNoOfAllowances.toString();
                }
                additionalAmountData = additionalAmountQuestion.reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );
              } else {
                exemptionTemp = exemptionsFromWithholding;
                if (exemptionTemp === 'exemption_from_withholding_two') {
                  stateOfDomicileTemp = stateOfDomicile;
                }
              }
              const lastNameDifferentOnSSNData = {
                last_name_different_on_social_security_card:
                  lastNameDifferentOnSSN,
              };
              const exemptionData = {
                is_exempt: isExemptFromWithholding,
                exemption: exemptionTemp,
                state_of_domicile: stateOfDomicileTemp,
              };
              const allowancesData = {
                total_number_of_allowances: totalNoOfAllowancesTemp,
                line_a_to_f: personalAllowancesTemp,
                file_yourself: fileForYourselfTemp,
                file_married_filing_jointly: fileAsMarriedJointlyTemp,
                file_head_of_household: fileAsHeadOfHouseholdTemp,
                file_others: fileOthersTemp,
                deductions_adjustments_additional_income_worksheet_value:
                  deductionsTemp,
              };
              const data = {
                ...stepOneData,
                ...lastNameDifferentOnSSNData,
                ...filingStatusData,
                ...worksheetData,
                ...exemptionData,
                ...allowancesData,
                ...additionalAmountData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderAllowances(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default LegacyForm;
